import { OverlayTrigger } from "react-bootstrap";
import { TextCss } from "../GlobalCss/TextCss";
import "./CurrencyTooltip.css";
import React from "react";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";

const CURRENCY_TOKEN = "[[currency]]";

const CurrencyTooltip = ({
  children,
  content,
  formatted,
  disabled = false,
}: {
  children?: React.ReactElement;
  content: string | number;
  formatted?: string | number;
  disabled?: boolean;
}) => {
  const currencySymbol = useCurrencySymbol();

  const computedDisabled = formatted !== undefined && formatted === content;

  const isDisabled = disabled || computedDisabled;

  // Detect if placeholder is used ANYWHERE
  const hasCurrencyToken =
    children &&
    React.Children.toArray(children.props.children).some(
      (child) => typeof child === "string" && child.includes(CURRENCY_TOKEN),
    );

  /* Detect if value is negative */
  const isNegative =
    typeof content === "number"
      ? content < 0
      : String(content).trim().match(/^[-−]/);

  // Format text helper
  const formatText = (val: string | number) => {
    const sVal = String(val);
    if (isNegative) {
      // Remove minus/hyphen/dash
      return `${currencySymbol}(${sVal.replace(/[-−]/g, "")})`;
    }
    return `${currencySymbol}${sVal}`;
  };

  // Helper to strip currency symbol and negative sign for uniform formatting
  const cleanValue = (val: string) => {
    // Remove current currency symbol, common symbols, and formatting marks (parentheses, negative signs)
    return val
      .replace(currencySymbol, "")
      .replace(/[$£€]/g, "")
      .replace(/[-−()]/g, "")
      .trim();
  };

  const isSvgElement =
    children &&
    (children.type === "text" ||
      children.type === "tspan" ||
      children.type === "g");

  const renderedChild = children ? (
    React.cloneElement(children, {
      className:
        isNegative && !isSvgElement
          ? (children.props.className || "")
              .split(" ")
              .filter(
                (c: string) =>
                  !["black", "grey_subtext1", "navyBlue", "dark_gray"].includes(
                    c,
                  ),
              )
              .join(" ")
          : children.props.className,
      style: {
        ...children.props.style,
        ...(isNegative
          ? isSvgElement
            ? { fill: "red" }
            : { color: "red" }
          : {}),
      },
      children: React.Children.map(children.props.children, (child) => {
        // STRING CHILD
        if (typeof child === "string") {
          // Explicit placeholder → replace
          if (child.includes(CURRENCY_TOKEN)) {
            let replaced = child.replace(/\[\[currency\]\]/g, currencySymbol);
            if (isNegative) {
              const cleaned = cleanValue(replaced);
              replaced = `${currencySymbol}(${cleaned})`;
            }
            return replaced;
          }

          // Fallback only if NO placeholder used
          if (!hasCurrencyToken) {
            if (isNegative) {
              const cleaned = cleanValue(child);
              return `${currencySymbol}(${cleaned})`;
            }
            return child.includes(currencySymbol)
              ? child
              : `${currencySymbol}${child}`;
          }
          return child;
        }

        // NUMBER CHILD
        if (typeof child === "number") {
          if (!hasCurrencyToken) {
            return isNegative
              ? `${currencySymbol}(${Math.abs(child)})`
              : `${currencySymbol}${child}`;
          }
          return child;
        }

        return child;
      }),
    })
  ) : (
    <span style={{ color: isNegative ? "red" : undefined }}>
      {formatText(content)}
    </span>
  );

  if (isDisabled) {
    return renderedChild;
  }

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 100, hide: 200 }}
      overlay={
        <div className="chart-tooltip">
          <div className={TextCss.black.sub_text_400_12}>
            {/* Wrap in span to enforce color priority over class */}
            <span style={{ color: isNegative ? "red" : "inherit" }}>
              {formatText(content)}
            </span>
          </div>
          <div className="chart-tooltip-arrow"></div>
        </div>
      }
    >
      {renderedChild}
    </OverlayTrigger>
  );
};

export default CurrencyTooltip;
